import { useEffect, useRef } from 'react';
import { Billing } from '../../interface/interfaces';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import './addaddress.scss';
import LoaderBtn from '../LoaderBtn/LoaderBtn';
import { Autocomplete } from '@react-google-maps/api';
import { allCountries } from 'country-telephone-data';

type Prop = {
 data: Billing;
 setData: React.Dispatch<React.SetStateAction<Billing>>;
 toggle: boolean;
 setToggle: React.Dispatch<React.SetStateAction<boolean>>;
 saveFunc: () => void;
 discardFunc: () => void;
 isSaving: boolean;
};

const AddAddress = ({
 data,
 toggle,
 isSaving,
 setData,
 setToggle,
 saveFunc,
 discardFunc,
}: Prop) => {
 const autocompleteRef =
  useRef<google.maps.places.Autocomplete | null>(null);
 const inputRef = useRef<HTMLInputElement>(null);
 const addressBox = useRef<HTMLDivElement>(null);

 // useEffect(() => {
 //     const handleClickOutside = (event: MouseEvent) => {
 //         const target = event.target as Node;

 //         const clickedOutsideBox = addressBox.current && !addressBox.current.contains(target);
 //         const clickedInAutocomplete = Array.from(document.querySelectorAll('.pac-container')).some((el) =>
 //             el.contains(target)
 //         );

 //         if (toggle && clickedOutsideBox && !clickedInAutocomplete) {
 //             setToggle(false);
 //         }
 //     };

 //     document.addEventListener("mousedown", handleClickOutside);
 //     return () => {
 //         document.removeEventListener("mousedown", handleClickOutside);
 //     };
 // }, [toggle]);
 function getDialCode(countryName: string): string | undefined {
  const match = allCountries.find(
   (country) =>
    country.name.toLowerCase() === countryName.toLowerCase(),
  );

  return match ? `+${match.dialCode}` : undefined;
 }

 useEffect(() => {
  if (!toggle) {
   const pacContainers = document.querySelectorAll(
    '.pac-container',
   ) as NodeListOf<HTMLElement>;
   pacContainers.forEach((pac) => {
    pac.style.borderTop = 'none';
   });
  }
 }, [toggle]);

 const handleInput = (value: string, name: string) => {
  setData((prev) => {
   return {
    ...prev,
    [name]: value,
   };
  });
 };

 const handlePhoneInput = (value: string) => {
  const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
  setData((prev) => ({ ...prev, phone: digitsOnly }));
 };

 const handleDialCode = (value: string) => {
  const digitsOnly = value.replace(/\D/g, '').slice(0, 4);
  setData((prev) => ({
   ...prev,
   countrycode: digitsOnly ? `+${digitsOnly}` : '',
  }));
 };

 const handlePlaceChanged = () => {
  const place = autocompleteRef.current?.getPlace();
  if (!place || !place.address_components) return;

  const getComponent = (types: string[]) =>
   place.address_components?.find((c) =>
    types.some((t) => c.types.includes(t)),
   )?.long_name || '';

  const address = place.formatted_address || '';
  const city = getComponent([
   'locality',
   'administrative_area_level_2',
  ]);
  const state = getComponent(['administrative_area_level_1']);
  const country = getComponent(['country']);
  const zip = getComponent(['postal_code']);

  setData((prev) => ({
   ...prev,
   address,
   city,
   state,
   country,
   zipcode: zip,
   countrycode: getDialCode(country) || '',
  }));

  // This line closes the dropdown
  inputRef.current?.blur();
 };

 return (
  <AnimatePresence>
   {toggle && (
    <motion.div
     className="addressInput"
     initial={{ opacity: 0 }}
     animate={{ opacity: 1 }}
     exit={{ opacity: 0 }}
    >
     <motion.div
      className="addressbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
       type: 'tween',
       duration: 0.2,
       ease: 'easeOut',
      }}
      ref={addressBox}
     >
      <div>
       <button
        className="close-button"
        onClick={() => setToggle((prev) => !prev)}
       >
        <X size={20}></X>
       </button>

       <div className="infopack">
        {data.id !== '' && (
         <div className="title">Edit Shipping Address</div>
        )}
        {data.id === '' && (
         <div className="title">Add Shipping Address</div>
        )}
        <div className="text">
         Kindly provide accurate information.
        </div>
       </div>

       <div className="inputTab">
        <div className="inputGrid">
         <div className="inputBox">
          <div className="label">First Name</div>
          <input
           className="input"
           value={data.firstname}
           onChange={(e) =>
            handleInput(e.currentTarget.value, 'firstname')
           }
           placeholder="Enter first name"
          ></input>
         </div>

         <div className="inputBox">
          <div className="label">Last Name</div>
          <input
           className="input"
           value={data.lastname}
           onChange={(e) =>
            handleInput(e.currentTarget.value, 'lastname')
           }
           placeholder="Enter last name"
          ></input>
         </div>

         {/* <div className="inputBox">
                                        <div className="label">Email <span>(Optional)</span></div>
                                        <input className="input" value={data.email} onChange={(e) => handleInput(e.currentTarget.value, 'email')} placeholder="Enter e-mail"></input>
                                    </div> */}
        </div>

        <div className="inputBox">
         <div className="label">Address</div>
         <Autocomplete
          onLoad={(auto) => (autocompleteRef.current = auto)}
          onPlaceChanged={handlePlaceChanged}
         >
          <input
           ref={inputRef}
           className="input"
           placeholder="Enter your address"
           value={data.address}
           onChange={(e) =>
            handleInput(e.currentTarget.value, 'address')
           }
          />
         </Autocomplete>
        </div>

        <div className="inputGrid">
         <div className="inputBox">
          <div className="label">Phone</div>

          <div className="phoneflex">
           <input
            className="countrycode"
            inputMode="numeric"
            value={data.countrycode}
            onChange={(e) => handleDialCode(e.currentTarget.value)}
            placeholder="+"
           ></input>
           <input
            className="input"
            inputMode="numeric"
            maxLength={10}
            value={data.phone}
            onChange={(e) => handlePhoneInput(e.currentTarget.value)}
            placeholder="10-digit phone"
           ></input>
          </div>
         </div>

         <div className="inputBox">
          <div className="label">Country</div>
          <input
           className="input"
           placeholder="Enter country"
           value={data.country}
           onChange={(e) =>
            handleInput(e.currentTarget.value, 'country')
           }
          ></input>

          {/* <select className="select" >
                                            {countries.map((data,index)=>{
                                                return (<option key={index} value={'nigeria'}>{data.name}</option>)
                                            })}
                               
                                        </select> */}
         </div>

         <div className="inputBox">
          <div className="label">State</div>
          <input
           className="input"
           placeholder="Enter state"
           value={data.state}
           onChange={(e) =>
            handleInput(e.currentTarget.value, 'state')
           }
          ></input>
         </div>

         <div className="inputBox">
          <div className="label">City</div>
          <input
           className="input"
           placeholder="Enter City"
           value={data.city}
           onChange={(e) =>
            handleInput(e.currentTarget.value, 'city')
           }
          ></input>
         </div>

         <div className="inputBox">
          <div className="label">Zipcode</div>
          <input
           className="input"
           placeholder="Enter Zipcode"
           value={data.zipcode}
           onChange={(e) =>
            handleInput(e.currentTarget.value, 'zipcode')
           }
          ></input>
         </div>
        </div>

        <hr className="line"></hr>

        <div className="bopack">
         <LoaderBtn
          className={'btn'}
          text={'Save Changes'}
          clickFunction={saveFunc}
          isLoading={isSaving}
         ></LoaderBtn>
         {/* <button className="btn" onClick={saveFunc}>Save Changes</button> */}
         <div
          className="crt_acc"
          onClick={discardFunc}
         >
          Cancel & Discard Changes
         </div>
        </div>
       </div>
      </div>
     </motion.div>
    </motion.div>
   )}
  </AnimatePresence>
 );
};

export default AddAddress;
